import { Button, DatePicker, Form, Select, Table } from 'antd'
import { FileSearchOutlined, FileExcelOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { Field } from 'formik'
import { Link } from 'react-router-dom'
import { useCallback, useMemo } from 'react'
import { createUseStyles } from 'react-jss'
import startCase from 'lodash/startCase'
import queryString from 'query-string'
import api from '../../api'
import { useAsync, useIsMobile } from '../../hooks'
import useExportExcel from '../../hooks/useExportExcel'
import useQueryParams from '../../hooks/useQueryParams'
import { getPaginationHeaders } from '../../redux/utils'
import { toPath } from '../../routes'
import { ListError, PageHeader } from '../Common'
import Filters, { FIELDS_OPTIONS } from '../Forms/Filters'
import { useIsAdmin, useCurrentUser } from '../../redux/slices/session'
import { useSelectedPractice } from '../../context/PracticeContext'
import {
  EXPAND_COLUMN_WIDTH,
  NotificationMobileCard,
  NotificationMobileDetails,
  formatAuthorName,
  formatNotificationDate,
} from './NotificationMobileRow'

const { Item } = Form
const { Option } = Select
const { RangePicker } = DatePicker

const NOTIFICATION_SUBTYPE_OPTIONS = [
  { label: 'Confirmation of Connectivity', value: 'confirmation' },
  {
    label: 'Request for Updated Patient Information',
    value: 'contact_information',
  },
  { label: 'Inactive Insurance', value: 'insurance_information' },
  { label: 'Non-Compliant', value: 'non_compliant' },
  { label: 'ERI/EOL', value: 'eri_eol' },
  { label: 'Explanted', value: 'explanted' },
  { label: 'Transferred Care', value: 'transferred_care' },
  { label: 'Patient Deceased', value: 'patient_deceased' },
  { label: 'Other', value: 'other' },
]

const DEFAULT_FILTERS = {
  query: '',
  deviceModelManufacturer: FIELDS_OPTIONS.deviceModelManufacturer[0].id,
}

const QUERY_PARAMS_SCHEMA = {
  page: Number,
}

const useStyles = createUseStyles({
  filterItem: {
    minWidth: 200,
  },
})

// Hoisted so the expanded row is not an inline component definition
// (react/no-unstable-nested-components).
const renderMobileDetails = () => notification =>
  <NotificationMobileDetails notification={notification} />

export default function Notifications() {
  const classes = useStyles()
  const isAdmin = useIsAdmin()
  const isMobile = useIsMobile()
  const user = useCurrentUser()
  const { selectedPracticeId } = useSelectedPractice()

  const [conditions, setConditions] = useQueryParams({
    initialValues: {
      page: 1,
      ...DEFAULT_FILTERS,
      // Seed practiceId so the first fetch is already scoped to the selected practice (#937).
      practiceId: isAdmin ? selectedPracticeId : user.practice.id,
    },
    schema: QUERY_PARAMS_SCHEMA,
  })

  const fetchNotifications = useCallback(
    () => api.notifications.list(conditions),
    [conditions],
  )

  const { pending, error, clearError, value } = useAsync(fetchNotifications)

  const notifications = useMemo(() => (value ? value.body : []), [value])
  const { pageSize, total } = value ? getPaginationHeaders(value.headers) : {}

  const exportUrl = useMemo(
    () =>
      queryString.stringifyUrl(
        {
          url: '/notifications.xlsx',
          query: {
            query: conditions.query,
            sub_type: conditions.subType,
            practice_id: conditions.practiceId,
            device_type_id: conditions.deviceTypeId,
            device_model_manufacturer: conditions.deviceModelManufacturer,
            medical_provider_id: conditions.medicalProviderId,
            office_location_id: conditions.officeLocationId,
            from_date: conditions.fromDate,
            to_date: conditions.toDate,
          },
        },
        { skipNull: true, skipEmptyString: true },
      ),
    [conditions],
  )

  const [exportingExcel, exportExcel] = useExportExcel(exportUrl)

  const handlePageChange = useCallback(
    page => setConditions({ ...conditions, page }),
    [conditions, setConditions],
  )

  const handleSubmit = useCallback(
    ({
      practiceId,
      deviceTypeId,
      deviceModelManufacturer,
      query,
      medicalProviderId,
      officeLocationId,
      subType,
    }) => {
      setConditions({
        ...conditions,
        page: 1,
        practiceId,
        deviceTypeId,
        deviceModelManufacturer,
        query,
        medicalProviderId,
        officeLocationId,
        subType,
      })
    },
    [conditions, setConditions],
  )

  const handleDateRangeChange = useCallback(
    dates => {
      if (!dates) {
        setConditions({
          ...conditions,
          fromDate: undefined,
          toDate: undefined,
          page: 1,
        })
        return
      }
      setConditions({
        ...conditions,
        fromDate: dates[0].format('YYYY-MM-DD'),
        toDate: dates[1].format('YYYY-MM-DD'),
        page: 1,
      })
    },
    [conditions, setConditions],
  )

  const dateRangeValue = useMemo(() => {
    if (!conditions.fromDate || !conditions.toDate) return undefined
    return [dayjs(conditions.fromDate), dayjs(conditions.toDate)]
  }, [conditions.fromDate, conditions.toDate])

  const subtypeFilter = (
    <Field name="subType">
      {({ field, form }) => (
        <Item className={classes.filterItem}>
          <Select
            allowClear
            placeholder="Notification type"
            style={{ width: '100%' }}
            value={field.value || undefined}
            onChange={val => form.setFieldValue('subType', val || null)}
          >
            {NOTIFICATION_SUBTYPE_OPTIONS.map(opt => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </Item>
      )}
    </Field>
  )

  const extraFilters = (
    <>
      {subtypeFilter}
      <Item className={classes.filterItem}>
        <RangePicker
          allowClear
          value={dateRangeValue}
          onChange={handleDateRangeChange}
          ranges={{
            'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
            'Last Month': [
              dayjs().subtract(1, 'month').startOf('month'),
              dayjs().subtract(1, 'month').endOf('month'),
            ],
          }}
        />
      </Item>
    </>
  )

  const columns = [
    {
      title: 'Patient',
      key: 'patient',
      render(_, notification) {
        if (!notification.patient) return '-'
        return (
          <Link to={toPath.patients.patient({ id: notification.patient.id })}>
            {notification.patient.lastName}, {notification.patient.firstName}
          </Link>
        )
      },
    },
    {
      title: 'Type',
      dataIndex: ['notificationType'],
      key: 'notificationType',
      render: text => startCase(text),
    },
    {
      title: 'Sub-type',
      dataIndex: ['subType'],
      key: 'subType',
      render: text => startCase(text),
    },
    {
      title: 'Title',
      dataIndex: ['title'],
      key: 'title',
    },
    {
      title: 'Sent by',
      key: 'author',
      render(_, notification) {
        return formatAuthorName(notification.author) || '-'
      },
    },
    {
      title: 'Date',
      dataIndex: ['createdAt'],
      key: 'createdAt',
      render(_, notification) {
        return formatNotificationDate(notification.createdAt) || '-'
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render(_, notification) {
        return (
          <Link to={toPath.notifications.notification({ id: notification.id })}>
            <Button type="link" icon={<FileSearchOutlined />} />
          </Link>
        )
      },
    },
  ]

  const mobileColumns = useMemo(
    () => [
      {
        title: 'Notification',
        key: 'notification',
        render(_, notification) {
          return <NotificationMobileCard notification={notification} />
        },
      },
    ],
    [],
  )

  const mobileExpandable = useMemo(
    () => ({
      columnWidth: EXPAND_COLUMN_WIDTH,
      expandedRowRender: renderMobileDetails(),
    }),
    [],
  )

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
      <PageHeader title="Notifications" />
      <Filters
        currentValues={conditions}
        onSubmit={handleSubmit}
        showPractice={isAdmin}
        showDeviceType
        showSearch
        showDeviceManufacturer
        showOfficeLocation
        showMedicalProvider
        extraFilter={extraFilters}
        placeholders={{
          query: 'Search by name, serial number or practice MRN',
        }}
      />
      <Table
        size={isMobile ? 'small' : undefined}
        columns={isMobile ? mobileColumns : columns}
        dataSource={notifications}
        showHeader={!isMobile}
        expandable={isMobile ? mobileExpandable : undefined}
        loading={pending}
        pagination={{
          current: conditions.page || 1,
          pageSize,
          onChange: handlePageChange,
          total,
        }}
        rowKey="id"
      />
      {Boolean(notifications.length) && (
        <div>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </div>
  )
}
